'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { constructQuery } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import TasksTabById from 'src/parts/tabs/TasksTabById';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getEventTasks } from 'src/api/personal/eventDetail';

interface TasksSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/personal_/events/$eventId/tasks/')({
	validateSearch: (search: Record<string, unknown>): TasksSearch => ({
		page: (search.page as number) || undefined,
		sort: search.sort as string | undefined,
	}),
	component: TasksPage,
});

function TasksPage() {
	const { eventId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const query = constructQuery({ page, sort });
	const { data: tasksData, isLoading: tasksLoading } = useQuery({
		queryKey: ['personal-event-tasks', eventId, page, sort],
		queryFn: () => getEventTasks(eventId, query),
	});

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event-tasks', eventId] });
	};

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-event-tasks', eventId] });
	};

	const handleTaskSelected = (id: string) => {
		navigate({
			to: '/personal/events/$eventId/tasks/$taskId',
			params: { eventId, taskId: id },
		});
	};

	const isLoading = authLoading || tasksLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!authenticatedUser) return null;

	return (
		<TasksTabById
			tasks={tasksData}
			authenticatedUser={authenticatedUser}
			onUpdate={handleUpdate}
			onReload={handleReload}
			onTaskSelected={handleTaskSelected}
		/>
	);
}
